import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Activity, Search, Plus, Filter, Calendar as CalendarIcon, ArrowLeft, Edit2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import PatientSummaryCard from "@/components/pilates/PatientSummaryCard";
import LessonPlannerForm from "@/components/pilates/LessonPlannerForm";
import AIGenerationLoader from "@/components/pilates/AIGenerationLoader";
import LessonCard from "@/components/pilates/LessonCard";
import ExerciseList from "@/components/pilates/ExerciseList";
import ExerciseEditModal from "@/components/pilates/ExerciseEditModal";
import PlanEditModal from "@/components/pilates/PlanEditModal";

interface Patient {
  id: string;
  full_name: string;
  cpf: string | null;
  avatar_url: string | null;
  data_nascimento: string | null;
}

interface LessonPlan {
  id: string;
  patient_id: string;
  total_lessons: number;
  frequency: string;
  start_date: string;
  status: string;
  notes: string | null;
  created_at: string;
  patient_snapshot: any;
  ai_recommendations: any;
}

interface Exercise {
  id: string;
  lesson_number: number;
  day_of_week: string;
  scheduled_date: string;
  is_completed: boolean;
  exercise_name: string;
  equipment: string | null;
  sets: number | null;
  reps: string | null;
  springs: string | null;
  focus_area: string | null;
  difficulty: string;
  notes: string | null;
  ai_reasoning: string | null;
  display_order: number;
}

export default function PilatesPlannerExtension() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState<Patient[]>([]);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [patientSummary, setPatientSummary] = useState<any>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [lessonPlans, setLessonPlans] = useState<LessonPlan[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<LessonPlan | null>(null);
  const [selectedLesson, setSelectedLesson] = useState<number | null>(null);
  const [lessonExercises, setLessonExercises] = useState<Exercise[]>([]);
  const [showNewPlanForm, setShowNewPlanForm] = useState(false);
  const [editingExercise, setEditingExercise] = useState<Exercise | null>(null);
  const [deletingExerciseId, setDeletingExerciseId] = useState<string | null>(null);
  const [editingPlan, setEditingPlan] = useState<LessonPlan | null>(null);
  const [deletingPlanId, setDeletingPlanId] = useState<string | null>(null);

  useEffect(() => {
    loadLessonPlans();
  }, []);

  const loadLessonPlans = async () => {
    try {
      const { data, error } = await supabase
        .from("pilates_lesson_plans")
        .select("*, patients_onboarding(full_name, avatar_url)")
        .order("created_at", { ascending: false });

      if (error) throw error;
      setLessonPlans(data || []);
    } catch (error) {
      console.error("Erro ao carregar planos:", error);
      toast.error("Erro ao carregar planos de aula");
    }
  };

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      setSearchResults([]);
      return;
    }

    try {
      const { data, error } = await supabase.rpc("fuzzy_search_patients", {
        search_query: searchTerm,
        search_cpf: searchTerm,
      });

      if (error) throw error;

      // Buscar dados completos dos pacientes
      if (data && data.length > 0) {
        const patientIds = data.map((p: any) => p.id);
        const { data: patientsData } = await supabase
          .from("patients_onboarding")
          .select("*")
          .in("id", patientIds);

        setSearchResults(patientsData || []);
      } else {
        setSearchResults([]);
      }
    } catch (error) {
      console.error("Erro na busca:", error);
      toast.error("Erro ao buscar pacientes");
    }
  };

  const handleSelectPatient = async (patient: Patient) => {
    setSelectedPatient(patient);
    setSearchResults([]);
    setSearchTerm("");

    // Buscar dados completos do paciente
    try {
      // ✅ REMOVIDO: patient_health_records não é mais usado
      const healthRecords: any[] = [];

      const { data: aiAnalysis } = await supabase
        .from("ai_global_analysis_cache")
        .select("json_result")
        .eq("patient_id", patient.id)
        .single();

      const conditions = healthRecords?.map((r) => r.title) || [];
      const analysisData = aiAnalysis?.json_result as any;
      const healthScore = analysisData?.healthScore || 0;
      const lastRecord = healthRecords?.[0]
        ? { title: healthRecords[0].title, date: healthRecords[0].created_at }
        : null;

      setPatientSummary({
        ...patient,
        conditions,
        healthScore,
        lastRecord,
        alerts: ["Evitar hiperextensão lombar"],
        aiRecommendations: ["Fortalecimento do core", "Mobilidade de coluna"],
      });
    } catch (error) {
      console.error("Erro ao carregar dados do paciente:", error);
    }
  };

  const handleGeneratePlan = async (config: any) => {
    if (!selectedPatient) {
      toast.error("Selecione um aluno primeiro");
      return;
    }

    setIsGenerating(true);

    try {
      const { data, error } = await supabase.functions.invoke("generate-pilates-lesson-plan", {
        body: {
          patientId: selectedPatient.id,
          totalLessons: config.totalLessons,
          frequency: config.frequency,
          startDate: config.startDate,
          notes: config.notes,
        },
      });

      if (error) throw error;

      toast.success("Plano de aulas gerado com sucesso!");

      // Limpar formulário
      setSelectedPatient(null);
      setPatientSummary(null);
      setShowNewPlanForm(false);

      // Recarregar planos
      await loadLessonPlans();
    } catch (error: any) {
      console.error("Erro ao gerar plano:", error);
      toast.error(error.message || "Erro ao gerar plano de aulas");
    } finally {
      setIsGenerating(false);
    }
  };

  const loadLessonDetails = async (planId: string, lessonNumber: number) => {
    try {
      const { data, error } = await supabase
        .from("pilates_exercises")
        .select("*")
        .eq("lesson_plan_id", planId)
        .eq("lesson_number", lessonNumber)
        .order("display_order");

      if (error) throw error;
      setLessonExercises(data || []);
    } catch (error) {
      console.error("Erro ao carregar exercícios:", error);
      toast.error("Erro ao carregar exercícios da aula");
    }
  };

  const handleOpenLesson = async (plan: LessonPlan, lessonNumber: number) => {
    setSelectedPlan(plan);
    setSelectedLesson(lessonNumber);
    await loadLessonDetails(plan.id, lessonNumber);
  };

  const handleMarkLessonComplete = async () => {
    if (!selectedPlan || selectedLesson === null) return;

    try {
      const { error } = await supabase
        .from("pilates_exercises")
        .update({ is_completed: true, completed_at: new Date().toISOString() })
        .eq("lesson_plan_id", selectedPlan.id)
        .eq("lesson_number", selectedLesson);

      if (error) throw error;

      toast.success("Aula marcada como realizada!");
      setSelectedLesson(null);
      setSelectedPlan(null);
      await loadLessonPlans();
    } catch (error) {
      console.error("Erro ao marcar aula:", error);
      toast.error("Erro ao marcar aula como realizada");
    }
  };

  const handleEditExercise = (exercise: Exercise) => {
    setEditingExercise(exercise);
  };

  const handleSaveExercise = async (exerciseId: string, data: any) => {
    try {
      const { error } = await supabase
        .from("pilates_exercises")
        .update({
          exercise_name: data.exercise_name,
          equipment: data.equipment || null,
          sets: data.sets || null,
          reps: data.reps || null,
          springs: data.springs || null,
          focus_area: data.focus_area || null,
          difficulty: data.difficulty,
          notes: data.notes || null,
          is_edited: true,
          original_exercise_name: editingExercise?.exercise_name,
        })
        .eq("id", exerciseId);

      if (error) throw error;

      toast.success("Exercício atualizado com sucesso!");

      // Recarregar exercícios da aula
      if (selectedPlan && selectedLesson !== null) {
        await loadLessonDetails(selectedPlan.id, selectedLesson);
      }
    } catch (error) {
      console.error("Erro ao atualizar exercício:", error);
      toast.error("Erro ao atualizar exercício");
    }
  };

  const handleDeleteExercise = (exerciseId: string) => {
    setDeletingExerciseId(exerciseId);
  };

  const confirmDeleteExercise = async () => {
    if (!deletingExerciseId) return;

    try {
      const { error } = await supabase
        .from("pilates_exercises")
        .delete()
        .eq("id", deletingExerciseId);

      if (error) throw error;

      toast.success("Exercício excluído com sucesso!");

      // Recarregar exercícios da aula
      if (selectedPlan && selectedLesson !== null) {
        await loadLessonDetails(selectedPlan.id, selectedLesson);
      }

      setDeletingExerciseId(null);
    } catch (error) {
      console.error("Erro ao excluir exercício:", error);
      toast.error("Erro ao excluir exercício");
    }
  };

  const handleEditPlan = (plan: LessonPlan) => {
    setEditingPlan(plan);
  };

  const handleSavePlan = async (planId: string, data: any) => {
    try {
      const { error } = await supabase
        .from("pilates_lesson_plans")
        .update(data)
        .eq("id", planId);

      if (error) throw error;

      toast.success("Plano atualizado com sucesso!");
      await loadLessonPlans();
      setEditingPlan(null);
    } catch (error) {
      console.error("Erro ao atualizar plano:", error);
      toast.error("Erro ao atualizar plano");
    }
  };

  const handleDeletePlan = (planId: string) => {
    setDeletingPlanId(planId);
  };

  const confirmDeletePlan = async () => {
    if (!deletingPlanId) return;

    try {
      // 1. Excluir exercícios do plano
      const { error: exercisesError } = await supabase
        .from("pilates_exercises")
        .delete()
        .eq("lesson_plan_id", deletingPlanId);

      if (exercisesError) throw exercisesError;

      // ✅ REMOVIDO: patient_health_records não é mais usado
      const recordsError = null;

      if (recordsError) throw recordsError;

      // 3. Excluir o plano
      const { error: planError } = await supabase
        .from("pilates_lesson_plans")
        .delete()
        .eq("id", deletingPlanId);

      if (planError) throw planError;

      toast.success("Plano excluído com sucesso!");
      setDeletingPlanId(null);
      await loadLessonPlans();
    } catch (error) {
      console.error("Erro ao excluir plano:", error);
      toast.error("Erro ao excluir plano");
    }
  };

  // Agrupar exercícios por aula
  const getLessonsForPlan = (planId: string) => {
    const exercises = lessonPlans.find((p) => p.id === planId);
    // Aqui você precisaria buscar os exercícios, mas para simplificar vamos criar um mock
    const lessons: any[] = [];
    return lessons;
  };

  return (
    <div className="space-y-6 p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/app/aplicativos")}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-gradient-to-br from-pink-100 to-rose-100 dark:from-pink-900/30 dark:to-rose-900/30">
              <Activity className="h-6 w-6 text-pink-600 dark:text-pink-400" />
            </div>
            <div>
              <h1 className="text-3xl font-bold tracking-tight">Planos de Aula · Pilates</h1>
              <p className="text-muted-foreground">
                Planeje aulas personalizadas com inteligência artificial
              </p>
            </div>
          </div>
        </div>
        <Button
          onClick={() => setShowNewPlanForm(!showNewPlanForm)}
          className="bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600"
        >
          <Plus className="mr-2 h-4 w-4" />
          Novo Plano
        </Button>
      </div>

      <Separator />

      {/* Novo Plano */}
      {showNewPlanForm && (
        <Card className="border-pink-200 dark:border-pink-800">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5 text-pink-500" />
              Novo Plano de Aulas
            </CardTitle>
            <CardDescription>
              Selecione um aluno e configure o planejamento
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Busca de paciente */}
            {!selectedPatient && (
              <div className="space-y-3">
                <div className="flex gap-2">
                  <Input
                    placeholder="🔍 Buscar aluno por nome ou CPF..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  />
                  <Button onClick={handleSearch}>
                    <Search className="h-4 w-4 mr-2" />
                    Buscar
                  </Button>
                </div>

                {searchResults.length > 0 && (
                  <div className="grid gap-2">
                    {searchResults.map((patient) => (
                      <Card
                        key={patient.id}
                        className="cursor-pointer hover:bg-accent transition-colors"
                        onClick={() => handleSelectPatient(patient)}
                      >
                        <CardContent className="p-4">
                          <p className="font-medium">{patient.full_name}</p>
                          {patient.cpf && (
                            <p className="text-sm text-muted-foreground">CPF: {patient.cpf}</p>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Resumo do paciente selecionado */}
            {selectedPatient && !isGenerating && (
              <>
                <PatientSummaryCard patient={patientSummary} />
                <Separator />
                <LessonPlannerForm
                  onGenerate={handleGeneratePlan}
                  isGenerating={isGenerating}
                />
              </>
            )}

            {/* Loader de geração */}
            {isGenerating && <AIGenerationLoader />}
          </CardContent>
        </Card>
      )}

      {/* Lista de Planos */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-semibold flex items-center gap-2">
            <CalendarIcon className="h-5 w-5 text-pink-500" />
            Meus Planos
          </h2>
          <Button variant="outline" size="sm">
            <Filter className="h-4 w-4 mr-2" />
            Filtrar
          </Button>
        </div>

        {lessonPlans.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Activity className="h-12 w-12 text-muted-foreground/50 mb-3" />
              <p className="text-muted-foreground mb-4">
                Nenhum plano de aulas criado ainda
              </p>
              <Button
                onClick={() => setShowNewPlanForm(true)}
                className="bg-gradient-to-r from-pink-500 to-rose-500"
              >
                <Plus className="mr-2 h-4 w-4" />
                Criar Primeiro Plano
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {lessonPlans.map((plan) => (
              <PlanSummaryCard
                key={plan.id}
                plan={plan}
                onOpenLesson={handleOpenLesson}
                onEdit={handleEditPlan}
                onDelete={handleDeletePlan}
              />
            ))}
          </div>
        )}
      </div>

      {/* Sheet de detalhes da aula */}
      <Sheet
        open={selectedLesson !== null}
        onOpenChange={(open) => !open && setSelectedLesson(null)}
      >
        <SheetContent className="sm:max-w-3xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle>
              Aula {selectedLesson} · {lessonExercises[0]?.day_of_week}
            </SheetTitle>
            <SheetDescription>
              {lessonExercises[0]?.scheduled_date &&
                new Date(lessonExercises[0].scheduled_date).toLocaleDateString("pt-BR")}
            </SheetDescription>
          </SheetHeader>

          <div className="mt-6 space-y-6">
            {lessonExercises.length > 0 && (
              <>
                <div>
                  <h3 className="font-semibold mb-3">Exercícios</h3>
                  <ExerciseList
                    exercises={lessonExercises}
                    onEdit={handleEditExercise}
                    onDelete={handleDeleteExercise}
                  />
                </div>

                {lessonExercises[0]?.ai_reasoning && (
                  <div className="bg-pink-50 dark:bg-pink-950/20 rounded-lg p-4">
                    <h3 className="font-semibold mb-2 flex items-center gap-2">
                      <Activity className="h-4 w-4 text-pink-500" />
                      Justificativa IA
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {lessonExercises[0].ai_reasoning}
                    </p>
                  </div>
                )}

                {!lessonExercises[0]?.is_completed && (
                  <Button
                    onClick={handleMarkLessonComplete}
                    className="w-full bg-gradient-to-r from-green-500 to-emerald-500"
                  >
                    Marcar como Realizada ✓
                  </Button>
                )}
              </>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Modal de edição de exercício */}
      <ExerciseEditModal
        exercise={editingExercise}
        open={!!editingExercise}
        onClose={() => setEditingExercise(null)}
        onSave={handleSaveExercise}
      />

      {/* AlertDialog de confirmação de exclusão de exercício */}
      <AlertDialog open={!!deletingExerciseId} onOpenChange={() => setDeletingExerciseId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir este exercício? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteExercise}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Diálogo de confirmação de exclusão de plano */}
      <AlertDialog open={!!deletingPlanId} onOpenChange={() => setDeletingPlanId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão do plano</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação irá excluir permanentemente o plano de aulas, incluindo todas as aulas e
              exercícios associados. Os registros na timeline do paciente também serão removidos.
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeletePlan}
              className="bg-destructive hover:bg-destructive/90"
            >
              Excluir Plano
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Modal de edição de plano */}
      <PlanEditModal
        plan={editingPlan}
        open={!!editingPlan}
        onClose={() => setEditingPlan(null)}
        onSave={handleSavePlan}
      />
    </div>
  );
}

// Componente auxiliar para exibir resumo de cada plano
function PlanSummaryCard({
  plan,
  onOpenLesson,
  onEdit,
  onDelete,
}: {
  plan: any;
  onOpenLesson: (plan: any, lessonNumber: number) => void;
  onEdit: (plan: any) => void;
  onDelete: (planId: string) => void;
}) {
  const [exercises, setExercises] = useState<Exercise[]>([]);

  useEffect(() => {
    loadExercises();
  }, [plan.id]);

  const loadExercises = async () => {
    try {
      const { data } = await supabase
        .from("pilates_exercises")
        .select("*")
        .eq("lesson_plan_id", plan.id)
        .order("lesson_number");

      setExercises(data || []);
    } catch (error) {
      console.error("Erro ao carregar exercícios:", error);
    }
  };

  const lessonsByNumber = exercises.reduce((acc, ex) => {
    if (!acc[ex.lesson_number]) {
      acc[ex.lesson_number] = [];
    }
    acc[ex.lesson_number].push(ex);
    return acc;
  }, {} as Record<number, Exercise[]>);

  const lessons = Object.entries(lessonsByNumber).map(([num, exs]) => ({
    lessonNumber: parseInt(num),
    dayOfWeek: exs[0].day_of_week,
    scheduledDate: exs[0].scheduled_date,
    isCompleted: exs[0].is_completed,
    exercises: exs,
  }));

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle>{(plan as any).patients_onboarding?.full_name}</CardTitle>
            <CardDescription className="flex items-center gap-2 mt-1">
              <Badge variant="outline">{plan.total_lessons} aulas</Badge>
              <Badge variant="outline">{plan.frequency}/semana</Badge>
              <span className="text-xs">
                Iniciado em {new Date(plan.start_date).toLocaleDateString("pt-BR")}
              </span>
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Badge
              className={
                plan.status === "active"
                  ? "bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300"
                  : ""
              }
            >
              {plan.status === "active" ? "Ativo" : plan.status === "completed" ? "Concluído" : "Cancelado"}
            </Badge>
            <Button
              variant="ghost"
              size="icon"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(plan);
              }}
            >
              <Edit2 className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="text-destructive hover:text-destructive"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(plan.id);
              }}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex gap-3 overflow-x-auto pb-2">
          {lessons.map((lesson) => (
            <div key={lesson.lessonNumber} className="min-w-[140px]">
              <LessonCard
                lessonNumber={lesson.lessonNumber}
                dayOfWeek={lesson.dayOfWeek}
                scheduledDate={lesson.scheduledDate}
                isCompleted={lesson.isCompleted}
                exerciseCount={lesson.exercises.length}
                onClick={() => onOpenLesson(plan, lesson.lessonNumber)}
              />
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
